import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { apiService } from "../../../services/api";
import { toast } from "react-toastify";
import { ContratoDetalhado, ProdutoContrato } from "../../../types/contratos";
import { Municipio } from "../../../types/municipio";
import { usePermissions } from "../../../hooks/usePermissions";
import { ProtectedValue } from "../../../components/ProtectedValue";
import EmpenhosList from "./EmpenhosList";
import DocumentosEmpenho from "./DocumentosEmpenho";
import DocumentosOportunidadeTabs from "./components/DocumentosOportunidadeTabs";
import ItensOportunidade from "./components/ItensOportunidade"; // Novo import
import GestaoEstoqueContrato from "./components/GestaoEstoqueContrato"; // Import da gestão de estoque
import OrcamentosVisualizacao from "./components/OrcamentosVisualizacao";
import { formatCurrencyBRL } from "../../../utils/formatters";

const ContratoDetalhes = () => {
  const route = all_routes;
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Estados para os modais
  const [openModal, setOpenModal] = useState(false);
  const [orcamentosAtivado, setOrcamentosAtivado] = useState(false);

  // Estados para os dados
  const [loading, setLoading] = useState(true);
  const [contrato, setContrato] = useState<ContratoDetalhado | null>(null);
  const [contato, setContato] = useState<any>(null);
  const [municipio, setMunicipio] = useState<Municipio | null>(null);
  const [municipioNome, setMunicipioNome] = useState<string>("Não especificado");
  const [municipioUF, setMunicipioUF] = useState<string>("");
  const [oportunidade, setOportunidade] = useState<any>(null);

  // Função para formatar valores monetários do contrato/empenho/estoque (2 casas decimais)
  const formatCurrency = (value: number | string): string => formatCurrencyBRL(value, 2);

  // Função para formatar valores monetários de produtos da oportunidade (4 casas decimais)
  const formatCurrencyOportunidade = (value: number | string): string => formatCurrencyBRL(value, 4);

  // Função para formatar datas
  const formatDate = (dateString: string): string => {
    if (!dateString) return "-";
    const [datePart] = dateString.split('T');
    const [ano, mes, dia] = datePart.split('-');
    if (!ano || !mes || !dia) return "-";
    return `${dia}/${mes}/${ano}`;
  };

  const getNomeMunicipioComUF = () => {
    // Primeira prioridade: se temos o município carregado com UF
    if (municipio && municipio.nome && municipio.uf) {
      return `${municipio.nome}/${municipio.uf}`;
    }

    // Segunda prioridade: se temos nome e UF nos estados locais
    if (municipioNome !== "Não especificado" && municipioUF) {
      return `${municipioNome}/${municipioUF}`;
    }

    // Terceira prioridade: se temos o nome do município da oportunidade
    if (oportunidade?.nome_municipio) {
      return oportunidade.nome_municipio;
    }

    // Última opção: usar o que estiver definido nos estados locais
    return municipioNome;
  };

  // Função para carregar todos os dados necessários
  const loadAllData = async () => {
    if (!id) {
      toast.error("ID do contrato não fornecido");
      navigate(route.ContractsList);
      return;
    }

    setLoading(true);
    try {
      // Carregar dados do contrato
      const contratoId = parseInt(id);
      const contratoRes = await apiService.getContratoById(contratoId);
      setContrato(contratoRes.data);

      let municipioCarregado = false;

      // Tenta carregar o contato associado ao contrato
      if (contratoRes.data.id_licitante) {
        try {
          console.log("Buscando contato com ID:", contratoRes.data.id_licitante);
          const contatoRes = await apiService.getContatoById(contratoRes.data.id_licitante);

          if (contatoRes.success && contatoRes.data) {
            console.log("Dados do contato carregados:", contatoRes.data);
            setContato(contatoRes.data);

            // Verificar se o contato tem id_municipio
            if (contatoRes.data.id_municipio) {
              try {
                console.log("Buscando município com ID:", contatoRes.data.id_municipio);
                const municipiosRes = await apiService.getMunicipios({
                  id: contatoRes.data.id_municipio
                });

                if (municipiosRes.success && municipiosRes.data && municipiosRes.data.length > 0) {
                  console.log("Município encontrado:", municipiosRes.data[0]);
                  setMunicipio(municipiosRes.data[0]);
                  setMunicipioNome(municipiosRes.data[0].nome);
                  setMunicipioUF(municipiosRes.data[0].uf);
                  municipioCarregado = true;
                } else {
                  console.log("Nenhum município encontrado com o ID:", contatoRes.data.id_municipio);
                }
              } catch (error) {
                console.error("Erro ao carregar município do contato:", error);
              }
            } else {
              console.log("Contato não tem id_municipio definido");

              // Se o contato não tem id_municipio, vamos verificar se tem campos de endereço com a cidade
              if (contatoRes.data.municipio_nome) {
                console.log("Usando campo cidade do contato:", contatoRes.data.municipio_nome);
                setMunicipioNome(contatoRes.data.municipio_nome);
                setMunicipioUF(contatoRes.data.municipio_uf || "");
                municipioCarregado = true;
              }
            }
          }
        } catch (error) {
          console.error("Erro ao carregar contato:", error);
        }
      } else {
        console.log("Contrato não tem id_licitante definido");
      }

      // Se não conseguimos carregar o município a partir do contato, tentamos a partir da oportunidade
      if (!municipioCarregado && contratoRes.data.id_oportunidade) {
        try {
          console.log("Buscando oportunidade com ID:", contratoRes.data.id_oportunidade);
          const oportunidadeRes = await apiService.getOpportunityById(contratoRes.data.id_oportunidade);

          if (oportunidadeRes.data && oportunidadeRes.data.length > 0) {
            const oportunidadeData = oportunidadeRes.data[0];
            console.log("Dados da oportunidade:", oportunidadeData);
            setOportunidade(oportunidadeData);

            // Verificar se a oportunidade tem id_municipio para buscar detalhes completos
            if (oportunidadeData.id_municipio) {
              try {
                console.log("Buscando município da oportunidade com ID:", oportunidadeData.id_municipio);
                const municipiosRes = await apiService.getMunicipios({
                  id: oportunidadeData.id_municipio
                });

                if (municipiosRes.success && municipiosRes.data && municipiosRes.data.length > 0) {
                  console.log("Município da oportunidade encontrado:", municipiosRes.data[0]);
                  setMunicipio(municipiosRes.data[0]);
                  setMunicipioNome(municipiosRes.data[0].nome);
                  setMunicipioUF(municipiosRes.data[0].uf);
                  municipioCarregado = true;
                }
              } catch (error) {
                console.error("Erro ao carregar município da oportunidade:", error);
              }
            } else {
              console.log("Oportunidade não tem id_municipio definido");
            }

            // Se não tem id_municipio, vamos verificar se tem campo nome_municipio
            if (!municipioCarregado && oportunidadeData.municipio_nome) {
              console.log("Usando nome_municipio da oportunidade:", oportunidadeData.municipio_nome);
              setMunicipioNome(oportunidadeData.municipio_nome);
              setMunicipioUF("");
              municipioCarregado = true;
            }

            // Se ainda não carregamos, vamos tentar extrair do contato associado à oportunidade
            if (!municipioCarregado && oportunidadeData.id_contato) {
              try {
                console.log("Buscando contato da oportunidade com ID:", oportunidadeData.id_contato);
                const contatoOppRes = await apiService.getContatoById(oportunidadeData.id_contato);

                if (contatoOppRes.success && contatoOppRes.data) {
                  console.log("Dados do contato da oportunidade:", contatoOppRes.data);

                  if (contatoOppRes.data.id_municipio) {
                    try {
                      console.log("Buscando município do contato da oportunidade com ID:", contatoOppRes.data.id_municipio);
                      const municipiosOppRes = await apiService.getMunicipios({
                        id: contatoOppRes.data.id_municipio
                      });

                      if (municipiosOppRes.success && municipiosOppRes.data && municipiosOppRes.data.length > 0) {
                        console.log("Município do contato da oportunidade encontrado:", municipiosOppRes.data[0]);
                        setMunicipio(municipiosOppRes.data[0]);
                        setMunicipioNome(municipiosOppRes.data[0].nome);
                        setMunicipioUF(municipiosOppRes.data[0].uf);
                        municipioCarregado = true;
                      }
                    } catch (error) {
                      console.error("Erro ao carregar município do contato da oportunidade:", error);
                    }
                  } else if (contatoOppRes.data.municipio_nome) {
                    console.log("Usando campo cidade do contato da oportunidade:", contatoOppRes.data.municipio_nome);
                    setMunicipioNome(contatoOppRes.data.municipio_nome);
                    setMunicipioUF(contatoOppRes.data.municipio_uf || "");
                    municipioCarregado = true;
                  }
                }
              } catch (error) {
                console.error("Erro ao carregar contato da oportunidade:", error);
              }
            }

            // Como último recurso, podemos tentar extrair do título da oportunidade ou do contato_nome
            if (!municipioCarregado) {
              if (oportunidadeData.titulo && oportunidadeData.titulo.includes(" - ")) {
                const possibleCity = oportunidadeData.titulo.split(" - ")[0].trim();
                if (possibleCity.startsWith("Prefeitura de ")) {
                  const cityName = possibleCity.replace("Prefeitura de ", "").trim();
                  console.log("Extraindo município do título da oportunidade:", cityName);
                  setMunicipioNome(cityName);
                  setMunicipioUF("");
                  municipioCarregado = true;
                }
              } else if (oportunidadeData.contato_nome && oportunidadeData.contato_nome.startsWith("Prefeitura de ")) {
                const cityName = oportunidadeData.contato_nome.replace("Prefeitura de ", "").trim();
                console.log("Extraindo município do nome do contato da oportunidade:", cityName);
                setMunicipioNome(cityName);
                setMunicipioUF("");
                municipioCarregado = true;
              }
            }
          }
        } catch (error) {
          console.error("Erro ao carregar oportunidade:", error);
        }
      }

      // Se não foi possível carregar o município de nenhuma fonte
      if (!municipioCarregado) {
        console.log("Não foi possível carregar município de nenhuma fonte");
        // Verifica se pelo menos temos o nome do licitante/contato para tentar extrair uma cidade
        if (contratoRes.data.licitante_nome && contratoRes.data.licitante_nome.startsWith("Prefeitura de ")) {
          const cityName = contratoRes.data.licitante_nome.replace("Prefeitura de ", "").trim();
          console.log("Extraindo município do nome do licitante:", cityName);
          setMunicipioNome(cityName);
          setMunicipioUF("");
        } else {
          setMunicipioNome("Não especificado");
          setMunicipioUF("");
        }
      }

    } catch (error) {
      console.error("Erro ao carregar dados do contrato:", error);
      toast.error("Erro ao carregar dados do contrato");
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    loadAllData();
  }, [id]);

  // Calcular valores financeiros
  const calcularFinanceiros = () => {
    if (!contrato) return { total: 0, recebido: 0, aReceber: 0 };

    const total = contrato.valor_total || 0;
    // Usar valor_recebido do contrato se disponível, senão calcular dos empenhos
    const recebido = contrato.valor_recebido || (contrato.empenhos || [])
      .filter(emp => emp.situacao === "Entregue")
      .reduce((sum, emp) => sum + emp.valor_total, 0);
    const aReceber = total - recebido;

    return { total, recebido, aReceber };
  };

  const financeiros = calcularFinanceiros();

  // Dados para os cards financeiros
  const cardData = [
    {
      title: 'Pendente',
      value: formatCurrency(financeiros.aReceber),
      bgClass: 'bg-danger'
    },
    {
      title: 'Empenhado',
      value: formatCurrency(financeiros.recebido),
      bgClass: 'bg-success'
    },
    {
      title: 'Total do Contrato',
      value: formatCurrency(financeiros.total),
      bgClass: 'bg-teal'
    }
  ];

  // Deletar contrato
  const handleDeleteContrato = async () => {
    try {
      if (id) {
        await apiService.deleteContrato(parseInt(id));
        toast.success("Contrato excluído com sucesso!");
        navigate(route.ContractsList);
      }
    } catch (error) {
      console.error("Erro ao excluir contrato:", error);
      toast.error("Erro ao excluir contrato");
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12 text-center p-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p className="mt-2">Carregando dados do contrato...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!contrato) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12 text-center p-5">
              <div className="alert alert-danger">
                Contrato não encontrado
              </div>
              <Link to={route.ContractsList} className="btn btn-primary mt-3">
                Voltar para a lista
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Obter o número do contrato da oportunidade
  const numeroContrato = oportunidade?.numero_contrato || contrato.id.toString();
  // Nome do contato/licitante
  const nomeLicitante = contato?.nome || contrato.licitante_nome || "Não especificado";

  const nomeMunicipioComUF = getNomeMunicipioComUF();
  // Título formatado do contrato
  const tituloContrato = `CONTRATO Nº ${numeroContrato} - ${nomeLicitante}`;

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">Detalhes do Contrato</h4>
                  </div>
                  <div className="col-sm-8 text-sm-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {/* Informações do Contrato */}
              <div className="contact-head">
                <div className="row align-items-center">
                  <div className="col-sm-6">
                    <ul className="contact-breadcrumb">
                      <li>
                        <Link to={route.ContractsList}>
                          <i className="ti ti-arrow-narrow-left" />
                          Contratos
                        </Link>
                      </li>
                      <li>Contrato {numeroContrato}</li>
                    </ul>
                  </div>
                  <div className="col-sm-6 text-sm-end">
                    <div className="contact-pagination">
                      <ul>
                        <li>
                          <Link to="#" onClick={() => setOpenModal(true)}>
                            <i className="ti ti-trash text-danger" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-2">
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="d-flex align-items-center mb-2">
                      <div>
                        <h5 className="mb-1">{tituloContrato}</h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Informações do Contrato */}
            </div>

            {/* Alertas de Renovação */}
            {(contrato.id_contrato_original || contrato.id_contrato_renovado) && (
              <div className="row mb-3">
                <div className="col-md-12">
                  {contrato.id_contrato_original && (
                    <div className="alert alert-info d-flex align-items-center" role="alert">
                      <i className="ti ti-rotate-clockwise fs-4 me-2"></i>
                      <div className="flex-grow-1">
                        <strong>Contrato de Renovação</strong>
                        <p className="mb-0">
                          Este contrato é uma renovação.
                          {oportunidade?.numero_contrato && (
                            <> <strong> Contrato original: {oportunidade.numero_contrato}</strong></>
                          )}
                        </p>
                      </div>
                      <Link to={`/crm/contratos/detalhes/${contrato.id_contrato_original}`} className="btn btn-sm btn-outline-info">
                        <i className="ti ti-eye me-1"></i>
                        Ver Contrato Original
                      </Link>
                    </div>
                  )}
                  {contrato.id_contrato_renovado && (
                    <div className="alert alert-success d-flex align-items-center" role="alert">
                      <i className="ti ti-rotate-clockwise fs-4 me-2"></i>
                      <div className="flex-grow-1">
                        <strong>Contrato Renovado</strong>
                        <p className="mb-0">
                          Este contrato foi renovado e um novo contrato foi criado.
                        </p>
                      </div>
                      <Link to={`/crm/contratos/detalhes/${contrato.id_contrato_renovado}`} className="btn btn-sm btn-outline-success">
                        <i className="ti ti-eye me-1"></i>
                        Ver Contrato Renovado
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cards Financeiros */}
            <div className="row mb-4">
              {cardData.map((card, index) => (
                <div key={index} className="col-md-4">
                  <div className={`card ${card.bgClass} text-white`}>
                    <div className="card-body">
                      <h6 className="card-title text-white">{card.title}</h6>
                      <h2 className="card-text text-white font-weight-bold"><ProtectedValue module="CONTRATOS" value={card.value} /></h2>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* /Cards Financeiros */}

            {/* Menu Lateral */}
            <div className="col-xl-3 theiaStickySidebar">
              <div className="card">
                <div className="card-body p-3">
                  <h6 className="mb-3 fw-semibold">Informações Básicas</h6>
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                        <i className="ti ti-file-invoice" />
                      </span>
                      <p><strong>N° do contrato:</strong> {numeroContrato}</p>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                        <i className="ti ti-calendar-event" />
                      </span>
                      <p><strong>Data de início:</strong> {formatDate(contrato.data_inicio)}</p>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                        <i className="ti ti-calendar-due" />
                      </span>
                      <p><strong>Data de vencimento:</strong> {formatDate(contrato.data_finalizacao || '')}</p>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                        <i className="ti ti-building" />
                      </span>
                      <p><strong>Contato:</strong> {nomeLicitante}</p>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                        <i className="ti ti-map-pin" />
                      </span>
                      <p><strong>Município:</strong> {nomeMunicipioComUF}</p>
                    </div>
                    {/* Adicionando o campo de Compra Direta */}
                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                        <i className="ti ti-shopping-cart" />
                      </span>
                      <p>
                        <strong>Compra Direta:</strong>{" "}
                        {contrato.compra_direta ? (
                          <span className="badge bg-soft-info">Sim</span>
                        ) : (
                          <span className="badge bg-danger">Não</span>
                        )}
                      </p>
                    </div>
                    {/* Campo Tipo de Contrato */}
                    {contrato.tipo_contrato && (
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                          <i className="ti ti-rotate" />
                        </span>
                        <p>
                          <strong>Tipo:</strong>{" "}
                          {contrato.tipo_contrato === 'renovacao' ? (
                            <span className="badge bg-soft-info">Renovação</span>
                          ) : (
                            <span className="badge bg-success">Original</span>
                          )}
                        </p>
                      </div>
                    )}
                    {/* Campo Número de Renovações */}
                    {contrato.numero_renovacoes !== undefined && contrato.numero_renovacoes > 0 && (
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                          <i className="ti ti-rotate-clockwise" />
                        </span>
                        <p>
                          <strong>Renovações:</strong>{" "}
                          <span className="badge bg-soft-warning">{contrato.numero_renovacoes}x</span>
                        </p>
                      </div>
                    )}
                  </div>
                  <hr />
                  <h6 className="mb-3 fw-semibold">Status</h6>
                  <div className="mb-3">
                    <Link
                      to="#"
                      className={`badge badge-soft-${contrato.situacao === 'ativo' || contrato.situacao === 'Em andamento' ? 'success' : contrato.situacao === 'Finalizado com Renovação' ? 'primary' : 'warning'} fw-medium me-2`}
                    >
                      {contrato.situacao === 'ativo' ? 'Ativo' : contrato.situacao === 'Em andamento' ? 'Em andamento' : contrato.situacao || 'Sem status'}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* /Menu Lateral */}

            {/* Detalhes do Contrato */}
            <div className="col-xl-9">
              <div className="card mb-3">
                <div className="card-body pb-0 pt-2">
                  <ul className="nav nav-tabs nav-tabs-bottom" role="tablist">
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#empenhos"
                        className="nav-link active"
                      >
                        <i className="ti-receipt me-1" />
                        Empenhos
                      </Link>
                    </li>
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#documentosEmpenhos"
                        className="nav-link"
                      >
                        <i className="ti ti-file-invoice me-1" />
                        Docs de Empenhos
                      </Link>
                    </li>
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#documentosOportunidade"
                        className="nav-link"
                      >
                        <i className="ti-file me-1" />
                        Docs da Oportunidade
                      </Link>
                    </li>
                    {/* NOVA TAB - Itens Oportunidade */}
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#itensOportunidade"
                        className="nav-link"
                      >
                        <i className="ti-package me-1" />
                        Itens Oportunidade
                      </Link>
                    </li>
                    {/* NOVA TAB - Gestão de Estoque */}
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#gestaoEstoque"
                        className="nav-link"
                      >
                        <i className="ti ti-box me-1" />
                        Gestão de Estoque
                      </Link>
                    </li>
                    {/* TAB - Orçamentos */}
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#orcamentosContrato"
                        className="nav-link"
                        onClick={() => setOrcamentosAtivado(true)}
                      >
                        <i className="ti ti-file-dollar me-1" />
                        Orçamentos
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Tab Content */}
              <div className="tab-content pt-0">
                {/* Empenhos */}
                <div className="tab-pane active show" id="empenhos">
                  {contrato && (
                    <EmpenhosList
                      contratoId={parseInt(id || "0")}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                      onUpdateComplete={loadAllData}
                    />
                  )}
                </div>
                {/* /Empenhos */}

                {/* Documentos de Empenhos */}
                <div className="tab-pane fade" id="documentosEmpenhos">
                  {contrato && (
                    <DocumentosEmpenho
                      contratoId={parseInt(id || "0")}
                      formatDate={formatDate}
                      onUpdateComplete={loadAllData}
                    />
                  )}
                </div>
                {/* /Documentos de Empenhos */}

                {/* Documentos da Oportunidade */}
                <div className="tab-pane fade" id="documentosOportunidade">
                  {contrato && contrato.id_oportunidade && (
                    <DocumentosOportunidadeTabs
                      oportunidadeId={contrato.id_oportunidade}
                    />
                  )}
                </div>
                {/* /Documentos da Oportunidade */}

                {/* NOVA TAB - Itens Oportunidade */}
                <div className="tab-pane fade" id="itensOportunidade">
                  {contrato && contrato.produtos && contrato.produtos.length > 0 ? (
                    <ItensOportunidade
                      produtos={contrato.produtos}
                      formatCurrency={formatCurrencyOportunidade}
                      contratoId={contrato.id}
                    />
                  ) : (
                    <div className="alert alert-info m-3">
                      <i className="ti ti-info-circle me-2"></i>
                      Nenhum produto disponível para este contrato.
                    </div>
                  )}
                </div>
                {/* /Itens Oportunidade */}

                {/* NOVA TAB - Gestão de Estoque */}
                <div className="tab-pane fade" id="gestaoEstoque">
                  {contrato && contrato.id_oportunidade && (
                    <GestaoEstoqueContrato
                      oportunidadeId={contrato.id_oportunidade}
                      contratoId={contrato.id}
                      formatCurrency={formatCurrency}
                    />
                  )}
                </div>
                {/* /Gestão de Estoque */}

                {/* Orçamentos */}
                <div className="tab-pane fade" id="orcamentosContrato">
                  {orcamentosAtivado && contrato?.id_oportunidade && (
                    <OrcamentosVisualizacao oportunidadeId={contrato.id_oportunidade} />
                  )}
                </div>
                {/* /Orçamentos */}
              </div>
              {/* /Tab Content */}
            </div>
            {/* /Detalhes do Contrato */}
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Delete Confirmation Modal */}
      <Modal show={openModal} onHide={() => setOpenModal(false)}>
        <div className="modal-body">
          <div className="text-center">
            <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
              <i className="ti ti-trash-x fs-36 text-danger" />
            </div>
            <h4 className="mb-2">Remover Contrato?</h4>
            <p className="mb-0">
              Tem certeza que deseja remover <br /> este contrato?
            </p>
            <div className="d-flex align-items-center justify-content-center mt-4">
              <Link
                to="#"
                className="btn btn-light me-2"
                onClick={() => setOpenModal(false)}
              >
                Cancelar
              </Link>
              <button
                className="btn btn-danger"
                onClick={handleDeleteContrato}
              >
                Sim, Excluir
              </button>
            </div>
          </div>
        </div>
      </Modal>
      {/* /Delete Confirmation Modal */}
    </>
  );
};

export default ContratoDetalhes;